import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import StepIndicator from './components/StepIndicator';
import OnboardingSeo from './components/OnboardingSeo';
import Step1Details from './components/Step1Details';
import Step2Property from './components/Step2Property';
import Step3Court from './components/Step3Court';
import Step4Availability from './components/Step4Availability';
import Step5Photos from './components/Step5Photos';
import Step6Final from './components/Step6Final';

const FORM_URL = 'https://readdy.ai/api/form/d74ng4r41deorl02eueg';
const STORAGE_KEY = 'padelVillas_onboarding_progress';

const DEFAULT_FORM_DATA: Record<string, string> = {
  pullCopy: 'true',
  pullPhotos: 'true',
  pullAmenities: 'true',
};

interface SavedProgress {
  step: number;
  formData: Record<string, string>;
  savedAt: number;
}

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Record<string, string>>(DEFAULT_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resumed, setResumed] = useState(false);
  const [showResumeBar, setShowResumeBar] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);

  // Load saved progress on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved: SavedProgress = JSON.parse(raw);
        // Only restore if saved within the last 7 days
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - saved.savedAt < sevenDays && saved.step > 1) {
          setStep(saved.step);
          setFormData(saved.formData);
          setResumed(true);
          setShowResumeBar(true);
        }
      }
    } catch {
      // ignore corrupt storage
    }
  }, []);

  // Save progress to localStorage whenever step or formData changes
  useEffect(() => {
    if (submitted) return;
    try {
      const progress: SavedProgress = { step, formData, savedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
      // Flash the "saved" indicator
      setSaveFlash(true);
      const t = setTimeout(() => setSaveFlash(false), 1800);
      return () => clearTimeout(t);
    } catch {
      // ignore storage errors
    }
  }, [step, formData, submitted]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleNext = (nextStep: number) => {
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStep(1);
    setFormData(DEFAULT_FORM_DATA);
    setShowResumeBar(false);
    setResumed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      // Clear saved progress on success
      localStorage.removeItem(STORAGE_KEY);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f2ece2] flex flex-col items-center justify-center px-6 text-center">
        <OnboardingSeo />
        <div className="max-w-md w-full">
          <div className="w-20 h-20 flex items-center justify-center bg-[#234012] rounded-full mx-auto mb-8">
            <i className="ri-check-double-line text-[#fcfbf7] text-3xl" />
          </div>
          <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-4">Submission received</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#141413] mb-5 leading-tight">
            You&apos;re in the collection.
          </h1>
          <p className="text-[#3a3a34] text-base font-sans leading-relaxed mb-3">
            We&apos;ll be in touch within 48 hours.
          </p>
          <p className="text-[#b6862d] font-serif text-xl italic mb-10">Welcome to Padel Villas.</p>
          <Link
            to="/"
            className="inline-block bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Back to PadelVillas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2ece2] flex flex-col" data-readdy-form>
      <OnboardingSeo />

      {/* Resume banner */}
      {showResumeBar && resumed && (
        <div className="bg-[#234012] text-[#fcfbf7] px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm font-sans">
            <i className="ri-save-3-line text-[#b6862d]" />
            <span>Welcome back — we saved your progress. You&apos;re on step {step} of 6.</span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <button
              type="button"
              onClick={handleStartOver}
              className="text-[#fcfbf7]/60 hover:text-[#fcfbf7] text-xs font-sans transition-colors cursor-pointer whitespace-nowrap underline underline-offset-2"
            >
              Start over
            </button>
            <button
              type="button"
              onClick={() => setShowResumeBar(false)}
              className="w-6 h-6 flex items-center justify-center text-[#fcfbf7]/60 hover:text-[#fcfbf7] transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>
      )}

      {/* Progress bar at top */}
      <StepIndicator current={step} />

      {/* Main content area */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-6 md:px-12 xl:px-0 py-12 pb-20">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {step === 1 && (
          <Step1Details data={formData} onChange={handleChange} onNext={() => handleNext(2)} />
        )}
        {step === 2 && (
          <Step2Property data={formData} onChange={handleChange} onNext={() => handleNext(3)} onBack={() => handleNext(1)} />
        )}
        {step === 3 && (
          <Step3Court data={formData} onChange={handleChange} onNext={() => handleNext(4)} onBack={() => handleNext(2)} />
        )}
        {step === 4 && (
          <Step4Availability data={formData} onChange={handleChange} onNext={() => handleNext(5)} onBack={() => handleNext(3)} />
        )}
        {step === 5 && (
          <Step5Photos data={formData} onChange={handleChange} onNext={() => handleNext(6)} onBack={() => handleNext(4)} />
        )}
        {step === 6 && (
          <Step6Final data={formData} onChange={handleChange} onBack={() => handleNext(5)} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        )}
      </main>

      {/* Footer strip */}
      <div className="border-t border-[#e0d9ce] py-5 px-6 md:px-12 flex items-center justify-between gap-4">
        <p className="text-[#77776f] text-xs font-sans">&copy; {new Date().getFullYear()} PadelVillas</p>

        {/* Auto-save indicator */}
        <div className={`flex items-center gap-1.5 text-xs font-sans transition-all duration-500 ${saveFlash ? 'text-[#234012]' : 'text-[#77776f]/50'}`}>
          <i className={`ri-save-3-line transition-all duration-300 ${saveFlash ? 'text-[#b6862d]' : ''}`} />
          <span>Progress auto-saved</span>
        </div>

        <Link to="/" className="text-[#77776f] text-xs font-sans hover:text-[#3a3a34] transition-colors cursor-pointer whitespace-nowrap">
          &larr; Back to site
        </Link>
      </div>
    </div>
  );
}
